import { queryMessageList, markMessageRead } from "@/api/inbox";
import { MessageInfosItem } from "@/api/inbox/interface";
import { DirectionDown, DirectionUp } from "@/Icons";
import {
  Button,
  Divider,
  Drawer,
  EmptyState,
  Radio,
  Spin,
  Toast,
} from "@ad/anq-ui";
import { useLoadingFn } from "@afe/rocket-hooks";
import React, { useEffect, useState } from "react";
import moment from "moment";

import styles from "./index.module.scss";
import router from "next/router";
import { ResourceCodeEnums } from "@/api/dmp_common/interface";

interface Props {
  visible?: boolean;
  onClose?: () => void;
}

type TabKeyTypes = "isNotRead" | "all";

const DrawerMessage = ({ visible, onClose }: Props) => {
  const [messageList, setMessageList] = useState<MessageInfosItem[]>([]);
  const [currentType, setCurrentType] = useState<TabKeyTypes>("isNotRead");
  // message的内容的高度, 高于240则展示展开按钮
  const [messageContentHeightMap, setMessageContentHeightMap] = useState<
    Record<string, number>
  >({});
  const [messageExpandedMap, setMessageExpandedMap] = useState<
    Record<string, boolean>
  >({});

  useEffect(() => {
    if (visible) {
      getMessageList();
    }
  }, [currentType, visible]);

  const handleClose = () => {
    onClose();
  };

  const handleMarkRead = async (message?: MessageInfosItem) => {
    const res = await markMessageRead(
      message ? { recordIds: [message.recordId] } : { isAll: true }
    );

    if (res?.success) {
      getMessageList();
    } else {
      Toast.error("标记失败");
    }
  };

  const [messageLoading, getMessageList] = useLoadingFn(async () => {
    const res = await queryMessageList({
      isRead: currentType === "isNotRead" ? false : null,
      pageNo: 1,
      pageSize: 10,
    });

    if (res?.success) {
      setMessageList(res.result?.messageInfos || []);
    } else {
      Toast.error(res?.errorMsg || "获取消息列表失败");
    }
  });

  const getExpandButton = (item: MessageInfosItem) => {
    if (
      !messageContentHeightMap[item.recordId] ||
      messageContentHeightMap[item.recordId] < 240
    ) {
      return null;
    }

    if (messageExpandedMap[item.recordId]) {
      return (
        <div
          className={styles.unExpandButton}
          onClick={() =>
            setMessageExpandedMap({
              ...messageExpandedMap,
              [item.recordId]: false,
            })
          }
        >
          收起
          <DirectionUp style={{ marginLeft: 4 }} />
        </div>
      );
    }

    return (
      messageContentHeightMap[item.recordId] > 240 && (
        <>
          <div className={styles.expandShadow} />
          <div
            className={styles.expandButton}
            onClick={() =>
              setMessageExpandedMap({
                ...messageExpandedMap,
                [item.recordId]: true,
              })
            }
          >
            展开全文
            <DirectionDown style={{ marginLeft: 4 }} />
          </div>
        </>
      )
    );
  };

  return (
    <Drawer
      width={480}
      headerStyle={{ height: 58 }}
      title={
        <div>
          消息中心
          <Radio.Group
            value={currentType}
            onChange={(e) => setCurrentType(e.target.value)}
            size="small"
            style={{ marginLeft: 24 }}
          >
            <Radio.Button value="isNotRead" key="isNotRead">
              未读
            </Radio.Button>
            <Radio.Button value="all" key="all">
              全部
            </Radio.Button>
          </Radio.Group>
        </div>
      }
      onClose={handleClose}
      maskClosable
      visible={visible}
      footerButtons={[
        <Button
          type="primary"
          onClick={() => {
            const path = `/${ResourceCodeEnums.ACCOUNT_MANAGEMENT}/message`;
            onClose();
            if (!window.location.href.includes(path)) {
              router.push(path);
            }
          }}
          key="all"
        >
          查看所有消息
        </Button>,
        <Button onClick={() => handleMarkRead()} key="read">
          一键已读
        </Button>,
      ]}
      bodyStyle={{ marginBottom: 48, padding: 0 }}
    >
      <div className={styles.drawerMessageWrapper}>
        <Spin spinning={messageLoading}>
          {messageList.length ? (
            messageList.map((item) => (
              <div className={styles.messageItemWrapper} key={item.recordId}>
                <div className={styles.messageTitle}>
                  {item.topic}
                  {!item.isRead && (
                    <span
                      className={styles.markButton}
                      onClick={() => handleMarkRead(item)}
                    >
                      标记为已读
                    </span>
                  )}
                </div>
                <div className={styles.messageTime}>
                  {moment(item.time).format("YYYY/MM/DD HH:mm:ss")}
                  {item.isImportant && (
                    <>
                      <Divider type="vertical" style={{ margin: "0 8px" }} />
                      <span className={styles.messageImportant}>重要消息</span>
                    </>
                  )}
                </div>
                <div
                  className={styles.messageContentWrapper}
                  style={{
                    maxHeight: messageExpandedMap[item.recordId] ? 10000 : 268,
                  }}
                >
                  <div
                    className={styles.messageContent}
                    // 防止消息高度正好比最大高度(280)大一点点, 不足以容纳"展开全文"的高度(20)
                    style={{
                      minHeight: messageContentHeightMap[item.recordId]
                        ? messageContentHeightMap[item.recordId] + 20
                        : undefined,
                    }}
                    ref={(dom) => {
                      if (!dom) {
                        return;
                      }

                      // 记录每条消息的高度, 实现超出的展开按钮
                      if (!messageContentHeightMap[item.recordId]) {
                        messageContentHeightMap[item.recordId] =
                          dom.offsetHeight;
                        setMessageContentHeightMap({
                          ...messageContentHeightMap,
                        });
                      }
                    }}
                    dangerouslySetInnerHTML={{
                      __html: item.content.text,
                    }}
                  />
                  {getExpandButton(item)}
                </div>
                {item.content?.url && (
                  <Button
                    className={styles.handleButton}
                    onClick={() => window.open(item.content.url)}
                  >
                    {item.content.urlDesc}
                  </Button>
                )}
              </div>
            ))
          ) : (
            <div className={styles.messageEmptyWrapper}>
              <EmptyState description="暂无消息" />
            </div>
          )}
        </Spin>
      </div>
    </Drawer>
  );
};

export default DrawerMessage;
